import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { UserOctagonIcon } from '@/components/Icons'
import { PhoneNumberInput } from '@/components/PhonenumberInput'
import { UserSelect, type UserSelectValue } from '@/components/UserSelect'
import { useFormContext, Controller } from 'react-hook-form'
import type { EventFormDataType } from '@/schemas/eventForm.schema'
import { useQuery } from '@tanstack/react-query'
import { USER_QUERY_KEYS } from '@/api/queryKeys'
import { getRoles } from '@/api/userApi'
import { USER_ROLES } from '@/constants'
import { transformRolesToOptions } from '@/utils/helper'

export default function EventOrganizerForm() {
  const { control, watch, setValue, clearErrors, trigger } =
    useFormContext<EventFormDataType>()
  const organizer = watch('organizer')
  const organizerPhone = watch('organizerPhone')

  const { data: rolesData } = useQuery({
    queryKey: USER_QUERY_KEYS.roles(),
    queryFn: getRoles,
  })

  const roleOptions = transformRolesToOptions(rolesData)
  const eventAdminRoleOption = roleOptions.find(
    val => val.name === USER_ROLES.eventAdmin
  )

  const handlePhoneChange = (value: string | undefined) => {
    const phoneValue = value || ''
    if (phoneValue === '') {
      clearErrors(['organizerPhone'])
    }
    setValue('organizerPhone', phoneValue)
    setTimeout(() => {
      trigger('organizerPhone')
    }, 0)
  }

  const handleOrganizerChange = async (
    user: UserSelectValue | UserSelectValue[] | null
  ) => {
    clearErrors(['organizer', 'organizerEmail', 'organizerPhone'])
    if (user && Array.isArray(user)) {
      setValue('organizer', user[0])
      setValue('organizerEmail', user[0]?.email || '')
      setValue('organizerPhone', user[0]?.phoneNumber || '')
    } else {
      setValue('organizer', user)
      setValue('organizerEmail', user?.email || '')
      setValue('organizerPhone', user?.phoneNumber || '')
    }
    await trigger(['organizer', 'organizerEmail', 'organizerPhone'])
  }

  return (
    <>
      {/* Organizer Information */}
      <CardHeader className="pb-4 border-t border-b border-black/6 bg-ss-neutral-25 flex flex-row items-center gap-2 px-4 py-3">
        <UserOctagonIcon className="h-5 w-5 text-ss-neutral-600" />
        <CardTitle className="text-lg font-semibold text-ss-neutral-950">
          Organizer Information
        </CardTitle>
      </CardHeader>
      <CardContent className="p-4 space-y-4">
        {/* Organizer Name & Email */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Controller
              name="organizer"
              control={control}
              render={({ field, fieldState: { error } }) => (
                <>
                  <UserSelect
                    label="Organizer Name"
                    value={field.value}
                    onValueChange={handleOrganizerChange}
                    placeholder="Select organizer..."
                    required
                    className="w-full truncate"
                    statusFilter={['Active']}
                    roleFilter={
                      eventAdminRoleOption ? [eventAdminRoleOption.id] : []
                    }
                  />
                  {error && (
                    <p className="text-sm text-ss-critical-200 mt-1">
                      {error.message}
                    </p>
                  )}
                </>
              )}
            />
          </div>
          <div className="space-y-2">
            <Label
              variant="required"
              htmlFor="organizerEmail"
              className="text-sm font-medium text-ss-neutral-950"
            >
              Organizer Email
            </Label>
            <Controller
              name="organizerEmail"
              control={control}
              render={({ field, fieldState: { error } }) => (
                <>
                  <Input
                    {...field}
                    required
                    id="organizerEmail"
                    type="email"
                    placeholder="Enter organizer email"
                    className="h-11 border-ss-neutral-100 bg-ss-neutral-white focus:border-ss-blue-500 focus:ring-ss-blue-500/20"
                    disabled={!!organizer}
                  />
                  {error && (
                    <p className="text-sm text-ss-critical-200 mt-1">
                      {error.message}
                    </p>
                  )}
                </>
              )}
            />
          </div>
        </div>

        {/* Organizer Phone & Website */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Controller
              name="organizerPhone"
              control={control}
              render={({ fieldState: { error } }) => (
                <>
                  <PhoneNumberInput
                    label="Organizer Phone"
                    value={organizerPhone}
                    onChange={handlePhoneChange}
                    placeholder="Enter organizer phone"
                    className="w-full"
                    required
                    disabled={!!organizer}
                  />
                  {error && (
                    <p className="text-sm text-ss-critical-200 mt-1">
                      {error.message}
                    </p>
                  )}
                </>
              )}
            />
          </div>
          <div className="space-y-2">
            <Label
              htmlFor="organizerWebsite"
              className="text-sm font-medium text-ss-neutral-950"
            >
              Organizer Website
            </Label>
            <Controller
              name="organizerWebsite"
              control={control}
              render={({ field }) => (
                <Input
                  {...field}
                  id="organizerWebsite"
                  type="url"
                  placeholder="Enter organizer website"
                  className="h-11 border-ss-neutral-100 bg-ss-neutral-white focus:border-ss-blue-500 focus:ring-ss-blue-500/20"
                />
              )}
            />
          </div>
        </div>
      </CardContent>
    </>
  )
}
